import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useReportEffect } from "../../../../hooks/useReportEffect";
import {
  FilterV2Selection,
  selectionToNumericIds,
  useFilterContextV2,
} from "../../../../contexts/FilterContextV2";
import type { CurrentBalanceSummary } from "../../../../@types/Dashboard/CurrentBalanceSummary";
import "../styles.css";

export function InfoCattleMap() {
  const { t, i18n } = useTranslation();
  const { selectedAreas } = useFilterContextV2();
  const areaIds = useMemo(() => Array.from(new Set(selectionToNumericIds(selectedAreas))), [selectedAreas]);
  const [result, setResult] = useState<{ selection: FilterV2Selection; data: CurrentBalanceSummary }>();

  useReportEffect((axios) => {
    setResult(undefined);
    const controller = new AbortController();
    if (areaIds.length > 0) {
      axios.get<CurrentBalanceSummary>(`/balance/current-summary?areaIds=${areaIds.join(",")}`, {
        signal: controller.signal,
      }).then(({ data }) => {
        if (!controller.signal.aborted) setResult({ selection: selectedAreas, data });
      }).catch(() => {
        if (!controller.signal.aborted) setResult(undefined);
      });
    }
    return () => controller.abort();
  }, [selectedAreas]);

  // Hide the previous selection's values before its request effect runs.
  const summary = areaIds.length > 0 && result?.selection === selectedAreas ? result.data : undefined;
  const formatter = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2, useGrouping: false });
  const display = (value?: number | null) => value == null
    ? t("dashboardUi.currentSummaryUnavailable") : formatter.format(value);

  return (
    <section className="dashBoardInfo__container" style={{ marginTop: 30 }}>
      <div className="dashBoardInfoSplited__owner--container">
        <small>{t("dashboardUi.currentAdjustment")}</small>
        <h6 className={summary && summary.current_adjustment < 0
          ? "dashBoardAdjustment__negative" : "dashBoardAdjustment__positive"}>
          {display(summary?.current_adjustment)}
        </h6>
      </div>
      <div className="dashBoardInfo__dark--container">
        <small>{t("dashboardUi.expectedBalance")}</small>
        <h6>{display(summary?.expected_balance)}</h6>
      </div>
      <div className="dashBoardInfoGreen__owner--container">
        <small>{t("dashboardUi.stocking")}</small>
        <h6>{display(summary?.capacity_rate)}</h6>
      </div>
    </section>
  );
}
